import { useCallback, useEffect, useState } from 'react'

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material'
import { SynthesisPrompt } from 'constants/enums/SynthesisPromptEnum'
import isEmpty from 'lodash/isEmpty'

import { Idea } from 'models/ideaModels'

import { getPromptResponse } from 'services/completions.service'

import { BaseTextField } from 'components/Form'

import useApi from 'hooks/useApi'

import { getSubmittedIdeasPrompt } from 'prompts/submittedIdeasPrompt'

import SynthesisResponse from '../SynthesisResponse/SynthesisResponse'
import { styles } from './SynthesisSection.styles'

export type Props = {
	selectedIdeas: Idea[]
}

const SynthesisSection = ({ selectedIdeas = [] }: Props) => {
	const [aiResponse, setAiResponse] = useState('')
	const [genAIQuestion, setGenAIQuestion] = useState('')

	const { data, requestApi, loading } = useApi(getPromptResponse)

	const clearResponse = useCallback(() => {
		setAiResponse('')
	}, [])

	const onSynthesize = useCallback(() => {
		requestApi(getSubmittedIdeasPrompt(genAIQuestion, selectedIdeas))
	}, [requestApi, genAIQuestion, selectedIdeas])

	useEffect(() => {
		setAiResponse(data)
	}, [data])

	return (
		<Box>
			<Grid>
				<Grid container mb={1} mt={4}>
					<Grid item>
						<Typography variant="h5" color="primary.dark">
							Idea Analysis
						</Typography>
					</Grid>
				</Grid>{' '}
				<Grid container mb={1}>
					<Grid item>
						<Typography variant="caption" fontSize={'1.2rem'}>
							Checkmark the submitted ideas below that you want AI
							to analyze
						</Typography>
					</Grid>
				</Grid>
				<Grid container sx={styles.containerGrid} mb={1}>
					<Grid
						item
						container
						spacing={2}
						direction={{ xs: 'column', sm: 'row' }}
						sx={{ ...styles.flexGrid }}
					>
						<Grid item sx={styles.textFieldContainer}>
							<BaseTextField
								InputLabelProps={{ style: styles.inputLabel }}
								variant="outlined"
								data-testid={`submitted-ideas-synthesis-gen-ai-question-input`}
								label="Enter a prompt for AI to analyze the selected ideas..."
								value={genAIQuestion}
								onChangeValue={(val) => setGenAIQuestion(val)}
								required
								multiline
								size="small"
								sx={{
									...styles.textField,
								}}
							/>
						</Grid>
						<Grid item>
							<Button
								fullWidth
								color="secondary"
								variant="contained"
								size="large"
								data-testid={`submitted-ideas-synthesis-gen-ai-ask-question-button`}
								aria-label="ask AI"
								onClick={onSynthesize}
								startIcon={<AutoAwesomeIcon />}
								disabled={
									isEmpty(genAIQuestion) ||
									selectedIdeas.length <= 0
								}
							>
								Ask AI
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid container mb={1}>
					<Grid item>
						<Typography
							variant="caption"
							color="secondary.light"
							fontSize={'1.2rem'}
						>
							Example prompts:
						</Typography>
					</Grid>
				</Grid>
				<Grid container spacing={3}>
					<Grid item>
						<Button
							color={
								genAIQuestion === SynthesisPrompt.SUMMARIZE
									? 'info'
									: 'secondary'
							}
							variant="outlined"
							data-testid={`submitted-ideas-synthesis-button-preset-prompt-summarize`}
							onClick={() =>
								setGenAIQuestion(SynthesisPrompt.SUMMARIZE)
							}
							size="medium"
						>
							Summarize
						</Button>
					</Grid>
					<Grid item>
						<Button
							color={
								genAIQuestion === SynthesisPrompt.PRIORITIZE
									? 'info'
									: 'secondary'
							}
							variant="outlined"
							data-testid={`submitted-ideas-synthesis-button-preset-prompt-prioritize`}
							onClick={() =>
								setGenAIQuestion(SynthesisPrompt.PRIORITIZE)
							}
							size="medium"
						>
							Prioritize
						</Button>
					</Grid>
					<Grid item>
						<Button
							color={
								genAIQuestion === SynthesisPrompt.SYNTHESIZE
									? 'info'
									: 'secondary'
							}
							variant="outlined"
							data-testid={`submitted-ideas-synthesis-button-preset-prompt-synthesize`}
							onClick={() =>
								setGenAIQuestion(SynthesisPrompt.SYNTHESIZE)
							}
							size="medium"
						>
							Synthesize
						</Button>
					</Grid>
					<Grid item>
						<Button
							color={
								genAIQuestion ===
								SynthesisPrompt.SYNTHETIC_CUSTOMER_FEEDBACK
									? 'info'
									: 'secondary'
							}
							variant="outlined"
							data-testid={`submitted-ideas-synthesis-button-preset-prompt-synthetic-customer-feedback`}
							onClick={() =>
								setGenAIQuestion(
									SynthesisPrompt.SYNTHETIC_CUSTOMER_FEEDBACK
								)
							}
							size="medium"
						>
							Synthetic Customer Feedback
						</Button>
					</Grid>
				</Grid>
				{loading || !isEmpty(genAIQuestion) ? (
					<Grid container mb={2} mt={4}>
						<Grid item flexGrow={1}>
							{loading ? (
								<CircularProgress
									sx={styles.circularProgressIcon}
								/>
							) : !isEmpty(aiResponse) ? (
								<SynthesisResponse
									contentText={aiResponse}
									clearResponse={clearResponse}
								/>
							) : null}
						</Grid>
					</Grid>
				) : null}
			</Grid>
		</Box>
	)
}

export default SynthesisSection
